import React from 'react';
import { useSocket, clearPlayerSeat } from '../contexts/SocketContext';
import { useTranslations } from '../hooks/useTranslations';

/** Host-only room close / leave menu; rendered under the game rules control in PageWrapper. */
export const RoomHostActionsMenu: React.FC = () => {
  const { socket, currentRoom, myPlayerId } = useSocket();
  const t = useTranslations();
  const isHost = !!currentRoom && currentRoom.hostId === myPlayerId;
  const [hostRoomMenuOpen, setHostRoomMenuOpen] = React.useState(false);
  const hostRoomMenuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isHost) setHostRoomMenuOpen(false);
  }, [isHost]);

  React.useEffect(() => {
    if (!hostRoomMenuOpen) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (hostRoomMenuRef.current && !hostRoomMenuRef.current.contains(e.target as Node)) {
        setHostRoomMenuOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setHostRoomMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [hostRoomMenuOpen]);

  const handleLeaveRoom = () => {
    if (socket) {
      socket.emit('client:leave-room');
      clearPlayerSeat();
    }
  };

  const handleCloseRoom = () => {
    if (!socket || !currentRoom || currentRoom.hostId !== myPlayerId) return;
    if (!window.confirm(t.ui.closeRoomConfirm)) return;
    socket.emit('client:close-room');
  };

  if (!currentRoom || !isHost) return null;

  return (
    <div className="relative" ref={hostRoomMenuRef}>
      <button
        type="button"
        onClick={() => setHostRoomMenuOpen((o) => !o)}
        aria-expanded={hostRoomMenuOpen}
        aria-haspopup="menu"
        aria-controls="room-host-actions-menu"
        aria-label={t.ui.roomActionsMenu}
        className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-semibold hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-xs sm:text-sm"
      >
        {t.ui.roomActionsButton}
      </button>
      {hostRoomMenuOpen && (
        <div
          id="room-host-actions-menu"
          role="menu"
          aria-orientation="vertical"
          className="absolute right-0 top-full z-[70] mt-2 min-w-[11rem] w-max max-w-[min(100vw-2rem,20rem)] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-xl"
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setHostRoomMenuOpen(false);
              handleCloseRoom();
            }}
            className="flex w-full items-center px-4 py-3 text-left text-sm sm:text-base font-semibold text-red-600 hover:bg-red-50 transition-colors"
          >
            {t.ui.closeRoom}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setHostRoomMenuOpen(false);
              handleLeaveRoom();
            }}
            className="flex w-full items-center px-4 py-3 text-left text-sm sm:text-base font-semibold text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100"
          >
            {t.ui.leaveRoom}
          </button>
        </div>
      )}
    </div>
  );
};
